import { ReactNode } from "react";
import { Heading } from "src/api/sure-api/ssq/useGetOneSsq";
import { QuestionnairesList } from "src/components/QuestionnairesList/QuestionnairesList";
import { ANSWER_SSQ, PACKAGING, useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import { useGetPackagingPost } from "src/api/client-api/post/useGetPackagingPost";
import { usePackagingContext } from "../PackagingContext";
import { Loading } from "src/components/utils/Loading";
import { ExportSSQExcelButton } from "src/components/Button/ExportExcelSSQButton";
import { useTranslation } from "react-i18next";
import { Box, Stack, Typography } from "@mui/material";
import { ScoreProvider } from "src/context/ScoreContext";
import { ScoresSection } from "src/components/QuestionnairesList/Scores/ScoresSection";
import { getScoreIdsByType } from "src/utils/scores";
import { getVisibleHeadings } from "src/utils/ssq";
import { useTheme } from "@mui/material/styles";

export const Questionnaire = (): ReactNode => {
    const theme = useTheme();
    const { packaging, ssq, isSsqLoading } = usePackagingContext();
    const { selectedCompany, canUser, scoreTypes } = useFoodpilotContext();
    const { post, isLoading: isPostLoading } = useGetPackagingPost(packaging?.id);
    const { t } = useTranslation();

    if (!packaging) return <Loading />;
    if (selectedCompany === null) return;

    const companyScoresIds = getScoreIdsByType("packaging", scoreTypes);
    const visibleHeadings = ssq && getVisibleHeadings(ssq, post?.hiddenElements);
    const excelFilename = packaging.name;

    return (
        <>
            <Stack direction="row" alignItems="center" gap={theme.spacing(2)} width="100%">
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h2">{t("packagings.questionnaire")}</Typography>
                </Box>
                <ExportSSQExcelButton
                    label={t("ssq.export.excel.button.label")}
                    ssq={ssq}
                    post={post}
                    filename={excelFilename}
                />
            </Stack>
            <QuestionnairesList
                ssq={ssq}
                post={post}
                isSsqLoading={isSsqLoading}
                isPostLoading={isPostLoading}
                displayDateLastModified={false}
                completeQuestionnaireUrl={(heading: Heading) => {
                    const url = URLHandler.packaging.ssqHeading(selectedCompany.id, packaging.id, heading.id);
                    return url;
                }}
                scoresSection={
                    <ScoreProvider type="packaging" formattedScores={post?.formattedScores}>
                        <ScoresSection post={post} headings={visibleHeadings} scoresIds={companyScoresIds} />
                    </ScoreProvider>
                }
                enableTableHead={false}
                muiTableProps={{
                    sx: {
                        border: "none",
                    },
                }}
                tableMode="extended"
                canEdit={canUser(ANSWER_SSQ, PACKAGING)}
            />
        </>
    );
};
